import { useCallback, useEffect, useRef, useState } from 'react';
import {
  StartSessionResponse,
  TapsResponse,
  EndSessionResponse,
  TapLevel,
} from '@/domain/types';
import { addAcceptedTapsByLevel } from '@/domain/tapSession';
import { TAP_BATCH_INTERVAL_MS } from '@/domain/constants';
import { tapApi } from '@/services/tapApi';

export interface TapSessionState {
  isActive: boolean;
  sessionId: string | null;
  startTime: Date | null;
  endTime: Date | null;
  acceptedTaps: number;
  currentLevel: TapLevel;
  level1Taps: number;
  level2Taps: number;
  level3Taps: number;
  bonusTimeAwarded: number;
  estimatedPoints: number;
  isLoading: boolean;
  error: string | null;
}

export interface TapSessionActions {
  startSession: () => Promise<void>;
  registerTap: () => void;
  endSession: () => Promise<EndSessionResponse | null>;
  reset: () => void;
}

const initialState: TapSessionState = {
  isActive: false,
  sessionId: null,
  startTime: null,
  endTime: null,
  acceptedTaps: 0,
  currentLevel: 1,
  level1Taps: 0,
  level2Taps: 0,
  level3Taps: 0,
  bonusTimeAwarded: 0,
  estimatedPoints: 0,
  isLoading: false,
  error: null,
};

export function useTapSession(
  onSessionEnd?: (result: EndSessionResponse) => void
): [TapSessionState, TapSessionActions] {
  const [state, setState] = useState<TapSessionState>(initialState);

  const endingRef = useRef(false);
  const endSessionRef = useRef<() => Promise<EndSessionResponse | null>>(
    async () => null
  );

  // Pending taps to be sent in next batch
  const pendingTapsRef = useRef<number[]>([]);
  const batchIntervalRef = useRef<number | null>(null);
  const endTimeoutRef = useRef<number | null>(null);

  // Auto-end session when time is up (reschedules when endTime changes, e.g. bonus time).
  useEffect(() => {
    if (state.isActive && state.endTime) {
      const delayMs = Math.max(0, state.endTime.getTime() - Date.now());
      endTimeoutRef.current = window.setTimeout(() => {
        endTimeoutRef.current = null;
        endSessionRef.current();
      }, delayMs);

      return () => {
        if (endTimeoutRef.current) {
          clearTimeout(endTimeoutRef.current);
          endTimeoutRef.current = null;
        }
      };
    }
  }, [state.isActive, state.endTime]);

  // Send tap batches periodically
  useEffect(() => {
    if (state.isActive && state.sessionId) {
      batchIntervalRef.current = window.setInterval(async () => {
        const taps = pendingTapsRef.current;
        if (taps.length === 0) return;

        pendingTapsRef.current = [];

        try {
          const response = await tapApi.submitTaps({
            sessionId: state.sessionId!,
            tapCount: taps.length,
            timestamps: taps,
          });

          updateFromTapsResponse(response);
        } catch (err) {
          console.error('Failed to submit taps:', err);
        }
      }, TAP_BATCH_INTERVAL_MS);

      return () => {
        if (batchIntervalRef.current) {
          clearInterval(batchIntervalRef.current);
        }
      };
    }
  }, [state.isActive, state.sessionId]);

  const updateFromTapsResponse = (response: TapsResponse) => {
    setState((prev) => {
      // Accumulate accepted taps and points from batch response
      const newAcceptedTaps = prev.acceptedTaps + response.accepted;
      const newEstimatedPoints = prev.estimatedPoints + response.scoreDelta;
      const newLevel = response.level as TapLevel;

      // Backend attributes accepted taps to the level computed for this batch.
      const levelUpdates = addAcceptedTapsByLevel(
        {
          level1Taps: prev.level1Taps,
          level2Taps: prev.level2Taps,
          level3Taps: prev.level3Taps,
        },
        response.accepted,
        newLevel
      );

      return {
        ...prev,
        acceptedTaps: newAcceptedTaps,
        currentLevel: newLevel,
        ...levelUpdates,
        bonusTimeAwarded: prev.bonusTimeAwarded + response.bonusTimeAwarded,
        endTime: new Date(response.endsAt),
        estimatedPoints: newEstimatedPoints,
      };
    });
  };

  const startSession = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const response: StartSessionResponse = await tapApi.startSession();

      const endTime = new Date(response.endsAt);
      const startTime = new Date(endTime.getTime() - response.durationSeconds * 1000);

      setState({
        isActive: true,
        sessionId: response.sessionId,
        startTime,
        endTime,
        acceptedTaps: 0,
        currentLevel: 1,
        level1Taps: 0,
        level2Taps: 0,
        level3Taps: 0,
        bonusTimeAwarded: 0,
        estimatedPoints: 0,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to start session',
      }));
    }
  }, []);

  const registerTap = useCallback(() => {
    if (!state.isActive) return;
    pendingTapsRef.current.push(Date.now());
  }, [state.isActive]);

  useEffect(() => {
    endSessionRef.current = async (): Promise<EndSessionResponse | null> => {
      if (!state.sessionId) return null;
      if (endingRef.current) return null;
      endingRef.current = true;

      if (batchIntervalRef.current) {
        clearInterval(batchIntervalRef.current);
        batchIntervalRef.current = null;
      }
      if (endTimeoutRef.current) {
        clearTimeout(endTimeoutRef.current);
        endTimeoutRef.current = null;
      }

      // Send remaining taps first
      const remainingTaps = pendingTapsRef.current;
      if (remainingTaps.length > 0) {
        pendingTapsRef.current = [];
        try {
          await tapApi.submitTaps({
            sessionId: state.sessionId,
            tapCount: remainingTaps.length,
            timestamps: remainingTaps,
          });
        } catch {
          // Ignore errors on final batch
        }
      }

      try {
        const result = await tapApi.endSession(state.sessionId);

        setState((prev) => ({
          ...prev,
          isActive: false,
          estimatedPoints: result.pointsAwarded,
        }));

        onSessionEnd?.(result);
        return result;
      } catch (err) {
        console.error('Failed to end session:', err);
        setState((prev) => ({
          ...prev,
          isActive: false,
          error: err instanceof Error ? err.message : 'Failed to end session',
        }));
        return null;
      } finally {
        endingRef.current = false;
      }
    };
  }, [state.sessionId, onSessionEnd]);

  const endSession = useCallback(async (): Promise<EndSessionResponse | null> => {
    return endSessionRef.current();
  }, []);

  const reset = useCallback(() => {
    pendingTapsRef.current = [];
    if (batchIntervalRef.current) clearInterval(batchIntervalRef.current);
    if (endTimeoutRef.current) clearTimeout(endTimeoutRef.current);
    endingRef.current = false;
    setState(initialState);
  }, []);

  return [state, { startSession, registerTap, endSession, reset }];
}
